import { BaseService } from './BaseService';
import type {
  UsageAggregationDto,
  UsageSummaryDto,
  PagedUsageRecords,
  GetUsageRecordsParams,
  GetUsageAggregationsParams,
  GetUsageSummaryParams,
} from '../types/usage';

/**
 * Usage tracking service for handling usage and analytics operations.
 */
export class UsageService extends BaseService {
  /**
   * Gets usage records with optional filtering and pagination.
   */
  async getUsageRecords(params?: GetUsageRecordsParams): Promise<PagedUsageRecords> {
    const queryParams = new URLSearchParams();
    if (params?.projectId) queryParams.append('projectId', params.projectId);
    if (params?.usageType !== undefined) queryParams.append('usageType', params.usageType.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.agentDefinitionId) queryParams.append('agentDefinitionId', params.agentDefinitionId);
    if (params?.modelId) queryParams.append('modelId', params.modelId);
    if (params?.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());

    const url = `/usage/records${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<PagedUsageRecords>(url);
  }

  /**
   * Gets usage aggregations with optional filtering.
   */
  async getUsageAggregations(params?: GetUsageAggregationsParams): Promise<UsageAggregationDto[]> {
    const queryParams = new URLSearchParams();
    if (params?.projectId) queryParams.append('projectId', params.projectId);
    if (params?.usageType !== undefined) queryParams.append('usageType', params.usageType.toString());
    if (params?.period !== undefined) queryParams.append('period', params.period.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.agentDefinitionId) queryParams.append('agentDefinitionId', params.agentDefinitionId);
    if (params?.modelId) queryParams.append('modelId', params.modelId);

    const url = `/usage/aggregations${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<UsageAggregationDto[]>(url);
  }

  /**
   * Gets usage summary with breakdowns.
   */
  async getUsageSummary(params: GetUsageSummaryParams): Promise<UsageSummaryDto> {
    const queryParams = new URLSearchParams();
    if (params.projectId) queryParams.append('projectId', params.projectId);
    if (params.usageType !== undefined) queryParams.append('usageType', params.usageType.toString());
    queryParams.append('startDate', params.startDate);
    queryParams.append('endDate', params.endDate);

    const url = `/usage/summary?${queryParams.toString()}`;
    return this.get<UsageSummaryDto>(url);
  }
}

export const usageService = new UsageService();
